<template>
  <div class="tx-export-popup">
    <md-progress-bar v-if="loading"
                     class="tx-export-popup__progress"
                     md-mode="indeterminate"
    />

    <h2 class="tx-export-popup__title">
      {{ i18n.th_export_popup_title() }}
    </h2>
    <p class="tx-export-popup__text">
      {{ i18n.th_export_popup_text() }}
    </p>

    <form class="tx-export-popup__form" id="user-op-export-form" @submit.prevent="onSubmit">
      <div class="tx-export-popup__form-double">
        <date-field-flatpickr name="start-date"
                              id="export-form-start-date"
                              v-model="form.since"
                              :label="i18n.lbl_start_date()"
                              :disabled="loading"
        />
        <date-field-flatpickr name="end-date"
                              id="export-form-end-date"
                              v-model="form.to"
                              :label="i18n.lbl_end_date()"
                              :disabled="loading"
        />
      </div>

      <div class="tx-export-popup__form-double">
        <input-field name="wallet"
                     id="export-form-wallet"
                     v-model="form.wallet"
                     :label="i18n.lbl_my_wallet()"
                     :disabled="loading"

        />
      </div>

      <div class="tx-export-popup__form-double">
        <input-field name="external-hash"
                     id="export-form-external-hash"
                     v-model="form.hash"
                     :label="i18n.lbl_hash()"
                     :disabled="loading"
        />
      </div>

      <div class="tx-export-popup__form-double">
        <div class="tx-export-popup__form-amount">
          <select-field name="amount-op"
                        id="export-form-operator-op"
                        v-model="form.amount_op"
                        :values="AMOUNT_OPERATORS"
                        :disabled="loading"
                        v-validate="!isDoubleFilled ? 'required' : ''"
                        :errorMessage="!isDoubleFilled"
          />
          <input-field type="number"
                       name="amount"
                       id="export-form-amount"
                       v-model="form.amount"
                       :label="i18n.lbl_amount()"
                       :disabled="loading"
                       v-validate="!isDoubleFilled ? 'required' : ''"
                       :errorMessage="!isDoubleFilled"
          />
        </div>

        <select-field name="asset"
                      id="export-form-asset"
                      v-model="form.asset"
                      :label="i18n.lbl_choose_asset()"
                      :values="assets"
                      :disabled="loading"
        />
      </div>

      <div class="tx-export-popup__form-actions">
        <md-button type="button"
                   class="btn md-primary"
                   :disabled="loading"
                   @click="close"
        >
          {{ i18n.lbl_cancel() }}
        </md-button>
        <md-button type="submit"
                   class="btn md-primary md-yellow"
                   :disabled="loading"
        >
          {{ i18n.lbl_export() }}
        </md-button>
      </div>
    </form>
  </div>
</template>

<script>
import _ from 'lodash'
import {i18n} from '@js/i18n'
import {mapGetters} from 'vuex'
import {vuexTypes} from '@vuex/types'
import FormMixin from '@com/mixins/form.mixin'
import {formatDate} from '@utils/dates.util'
import {accountsService} from '@js/services/accounts.service'
import DateFieldFlatpickr from '@vue/common/fields/DateFieldFlatpickr.vue'
import InputField from '@vue/common/fields/InputField.vue'
import SelectField from '@vue/common/fields/SelectField.vue'

const DEAFULT_FILE_NAME = 'transactions.csv'
const AMOUNT_OPERATORS = ['', '=', '>=', '>', '<=', '<']

export default {
  components: {
    SelectField,
    InputField,
    DateFieldFlatpickr
  },
  mixins: [
    FormMixin
  ],
  data: () => ({
    i18n,
    loading: false,
    form: {
      since: null,
      to: null,
      wallet: null,
      hash: null,
      asset: null,
      amount: null,
      amount_op: null
    },
    AMOUNT_OPERATORS,
    DEAFULT_FILE_NAME
  }),
  computed: {
    ...mapGetters([
      vuexTypes.accountId,
      vuexTypes.userAcquiredTokens
    ]),
    assets () {
      return this.userAcquiredTokens.map(token => token.code)
    },
    isDoubleFilled () {
      return (this.form.amount && this.form.amount_op) || (!this.form.amount && !this.form.amount_op)
    }
  },
  methods: {
    close () {
      this.loading = false
      this.$emit('close')
    },
    async onSubmit () {
      if (!await this.isValid()) return

      this.loading = true

      try {
        const params = this.getParams()
        const res = await accountsService.loadCSV({
          id: this.accountId,
          params: params
        })

        if (res.data) {
          this.triggerDownload(res.data)
        } else {
          this.close()
        }
      } catch (error) {
        console.error(error)
        this.close()
      }

      setTimeout(() => {
        this.close()
      }, 100)
    },
    triggerDownload (data) {
      const blob = new Blob([data], {type: 'text/csv;charset=utf-8;'})
      const link = document.createElement('a')

      if (link.download !== undefined) {
        const url = URL.createObjectURL(blob)
        link.setAttribute('href', url)
        link.setAttribute('download', this.DEAFULT_FILE_NAME)
        document.body.appendChild(link)

        link.click()

        document.body.removeChild(link)
        URL.revokeObjectURL(url)
      }
    },
    getParams () {
      const params = _.cloneDeep(this.form)

      Object.keys(params).map(key => {
        if (params[key]) {
          if (key === 'since' || key === 'to') {
            params[key] = formatDate(params[key], 'YYYY-MM-DD')
          }
        } else {
          delete params[key]
        }
      })

      return params
    }
  }
}
</script>

<style lang="scss">
@import '~scss/variables';
@import '~scss/mixins';

.tx-export-popup {
  width: 100%;
  max-width: 640px;

  &__wrap {
    .md-dialog-container {
      width: 100%;
    }
  }

  &__progress {
    margin-bottom: 16px;
  }

  &__title {
    margin: 0 auto 10px;
    font-size: 28px;
    line-height: 150%;
    font-weight: 600;

    @include respond-to(small) {
      font-size: 24px;
      margin: 0 auto;
    }
  }

  &__text {
    margin: 10px auto;
    font-size: 16px;
    line-height: 24px;
    text-align: left;

    @include respond-to(small) {
      margin: 0 auto;
    }
  }

  &__form {
    margin-top: 24px;

    &-double {
      display: flex;
      align-items: center;
      gap: 32px;

      @include respond-to(small) {
        display: block;
      }

      & > * {
        width: 100%;
        max-width: unset;
        margin-bottom: 16px;
      }

      & > .date-field {
        max-width: unset;

        .date-field__field {
          height: 58px;
          width: auto;

          .date-field__input {
            padding: 12px 16px;
          }
        }
      }
    }

    &-amount {
      display: flex;
      align-items: center;
      margin-bottom: 0;
      gap: 1rem;

      & > *:first-child {
        width: 35%;
      }

      @include respond-to(small) {
        & > .md-field.md-theme-default {
          margin: 0;
        }
      }
    }

    &-actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 16px;
      margin-top: 24px;

      @include respond-to(small) {
        margin-top: 32px;
        justify-content: space-between;
      }
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/vue/app/history/index/components/TxExport.Popup.vue